/** Mapa dos estados do Brasil -> UF */
const UF_MAP = {
  acre: 'AC',
  alagoas: 'AL',
  amapa: 'AP',
  amazonas: 'AM',
  bahia: 'BA',
  ceara: 'CE',
  'distrito federal': 'DF',
  'espirito santo': 'ES',
  goias: 'GO',
  maranhao: 'MA',
  'mato grosso': 'MT',
  'mato grosso do sul': 'MS',
  'minas gerais': 'MG',
  para: 'PA',
  paraiba: 'PB',
  parana: 'PR',
  pernambuco: 'PE',
  piaui: 'PI',
  'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN',
  'rio grande do sul': 'RS',
  rondonia: 'RO',
  roraima: 'RR',
  'santa catarina': 'SC',
  'sao paulo': 'SP',
  sergipe: 'SE',
  tocantins: 'TO',
};
/** Converte o nome do estado em sigla, Ex:"Goiás" -> "GO"; mantém "GO" se já vier em UF. */
export function normalizeState(state) {
  if (!state) return '';
  const s = String(state).trim();
  if (s.length === 2) return s.toUpperCase();
  const key = s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return UF_MAP[key] || s.slice(0, 2).toUpperCase();
}
/** Mantém apenas dígitos e limita a 8 (CEP). */
export function normalizePostCode(cep) {
  if (!cep) return '';
  return String(cep).replace(/\D/g, '').slice(0, 8);
}
/** Remove tudo que não for dígito; retorna (ddd, number) */
export function splitBRPhone(phone) {
  let d = String(phone || '').replace(/\D/g, '');
  if (d.startsWith('55')) d = d.slice(2);
  if (d.length < 10) return { area_code: '', number: d };
  const area_code = d.slice(0, 2);
  const number = d.slice(2);
  return { area_code, number };
}
/** Normaliza cartão de crédito */
export function normalizePan(pan) {
  return String(pan || '').replace(/\D/g, '');
}
/** Validação Luhn (checksum) do número do cartão. */
export function isValidLuhn(pan) {
  const digits = normalizePan(pan);
  if (digits.length < 13 || digits.length > 19) return false;

  let sum = 0;
  let double = false;

  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let d = Number(digits[i]);
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }

  return sum % 10 === 0;
}
/** Adiciona mascara no número de cartão de crédito */
export function maskPan(pan) {
  const p = normalizePan(pan);
  if (p.length < 10) return '****';
  return `${p.slice(0, 6)}******${p.slice(-4)}`;
}
/** Normaliza e-mail para envio ao 3DS: minúsculo, sem espaços e sem acentos. */
export function normalizeEmailFor3DS(email) {
  if (!email) return '';
  return String(email)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
/** Telefone para 3DS: country_code=55 + subscriber (DDD+numero, 10–11 dígitos). */
export function normalizePhoneFor3DS(phone) {
  if (!phone) return '';
  let d = String(phone).replace(/\D/g, '');
  if (d.startsWith('55')) d = d.slice(2); // remove 55
  if (d.length > 11) d = d.slice(-11);
  return d; // <-- string
}
/** Converte "MM/YY" ou "MM/YYYY" em { exp_month, exp_year } */
export function parseExpiry(exp) {
  const [mmRaw, yyRaw] = String(exp || '').split('/');
  const exp_month = Number(String(mmRaw || '').trim());
  const yy = String(yyRaw || '').trim();
  const exp_year = Number(yy.length === 2 ? `20${yy}` : yy);
  return { exp_month, exp_year };
}
/** Converte "MM/YY" ou "MM/YYYY" em "YYYY-MM" (formato esperado em exp_date). */
export function toYYYYMM(exp) {
  if (!exp) return '';
  const [mm, yyRaw] = String(exp).split('/');
  const yy = yyRaw?.length === 2 ? `20${yyRaw}` : yyRaw;
  return `${yy}-${String(mm).padStart(2, '0')}`;
}
/** Carrega o script do 3DS v2 (Stone) */
export function load3DSScriptV2({
  isProduction,
  scriptId = 'stone-3ds-script',
  debugKey = '__stone3ds',
} = {}) {
  const src = isProduction
    ? 'https://3ds-nx-js.stone.com.br/live/v2/3ds2.min.js'
    : 'https://3ds-nx-js.stone.com.br/test/v2/3ds2.min.js';

  if (document.getElementById(scriptId)) return Promise.resolve(src);

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.src = src;

    script.onload = () => {
      window[debugKey] = {
        loaded: true,
        at: new Date().toISOString(),
        src,
        provider: 'stone',
        version: 'v2',
        env: isProduction ? 'live' : 'test',
      };
      resolve(src);
    };

    script.onerror = () => {
      window[debugKey] = {
        loaded: false,
        at: new Date().toISOString(),
        src,
        provider: 'stone',
        version: 'v2',
        env: isProduction ? 'live' : 'test',
        error: 'script.onerror',
      };
      reject(new Error(`Falha ao carregar script 3DS: ${src}`));
    };

    document.body.appendChild(script);
  });
}
/** Aguarda window.TDS (biblioteca v2) */
export function waitForTDS(timeoutMs = 20000, intervalMs = 100) {
  if (window.TDS && typeof window.TDS.init === 'function') {
    return Promise.resolve(true);
  }

  const startedAt = Date.now();

  return new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      if (window.TDS && typeof window.TDS.init === 'function') {
        clearInterval(timer);
        resolve(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(timer);
        reject(
          new Error(
            `TDS não carregado (timeout ${timeoutMs}ms). hasTDS=${!!window.TDS} ua=${
              navigator.userAgent
            }`
          )
        );
      }
    }, intervalMs);
  });
}

function extract3dsErrorDetail(err) {
  // Tenta pegar detail/status nos formatos mais comuns (axios/fetch/SDK)
  const status =
    err?.response?.status || err?.status || err?.data?.status || null;

  const detail =
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.data?.detail ||
    err?.message ||
    '';

  return { status, detail: String(detail || '') };
}

function isDataOnlyIneligible(err) {
  const { status, detail } = extract3dsErrorDetail(err);
  return Number(status) === 422 && /not eligible for data only/i.test(detail);
}

function forceFull3DSOrderData(orderData) {
  // Clona para não mutar o objeto original
  const clone = JSON.parse(JSON.stringify(orderData || {}));

  if (Array.isArray(clone?.payments)) {
    clone.payments = clone.payments.map(p => {
      const np = { ...(p || {}) };

      // Força purpose de pagamento "full"
      np.purpose = 'payment';

      // Remove qualquer flag “data only” (por precaução)
      delete np.data_only;
      delete np.dataOnly;
      delete np.notify_only;
      delete np.notifyOnly;

      return np;
    });
  }

  return clone;
}

/**
 * Executa o 3DS v2 via window.TDS.init(config, order).
 * Retorna o PRIMEIRO item do array: { tds_server_trans_id, trans_status, ... }.
 *
 * A doc define:
 * - precisa de tds_method_container_element e challenge_container_element
 * - retorna array com tds_server_trans_id + trans_status + authenticated_card + challenge_canceled
 */
export async function handle3DSAuthenticationV2({
  tdsToken,
  orderData,
  tdsMethodContainerEl,
  challengeContainerEl,
  challengeWindowSize = '03',
  useDefaultChallengeIframeStyle = true,
}) {
  if (!tdsToken) throw new Error('tdsToken ausente');

  // console.log('[3DS] handle3DSAuthenticationV2 orderData:', JSON.stringify(orderData));

  if (!tdsMethodContainerEl || !challengeContainerEl) {
    throw new Error('Containers 3DS não encontrados (tdsMethod/challenge)');
  }

  // console.log('[3DS] Iniciando autenticação...');

  // setIs3DSModalOpen?.(true);

  const run = async dataToSend => {
    const resp = await window.TDS.init(
      {
        token: tdsToken,
        tds_method_container_element: tdsMethodContainerEl,
        challenge_container_element: challengeContainerEl,
        use_default_challenge_iframe_style: useDefaultChallengeIframeStyle,
        challenge_window_size: challengeWindowSize,
      },
      dataToSend
    );

    const first = Array.isArray(resp) ? resp[0] : resp;
    if (!first) throw new Error('Resposta 3DS Vazia/Inesperada');
    /*
    const resultSummary = {
      trans_status: first?.trans_status,
      tds_server_trans_id: first?.tds_server_trans_id,
      challenge_canceled: first?.challenge_canceled,
      authenticated_card: first?.authenticated_card,
    };
    */

    // console.log('[3DS] Resultado:', JSON.stringify(resultSummary));

    return first;
  };

  try {
    const firstAttempt = await run(orderData);
    return firstAttempt;
  } catch (err) {
    if (!isDataOnlyIneligible(err)) {
      throw err;
    }

    console.warn('[3DS] Data Only não elegível. Reexecutando em FULL...', {
      originalError: extract3dsErrorDetail(err),
    });

    const fullOrderData = forceFull3DSOrderData(orderData);
    return run(fullOrderData);
  }
}

/**
 * Mapeia status 3DS (Y/N/C/U/A/R/I) para ação no checkout.
 * - proceed       → seguir/capturar (liability shift quando Y/A conforme arranjo)
 * - proceed_risk  → seguir por conta e risco do lojista (sem 3DS efetivo)
 * - challenge     → repetir/garantir a execução do desafio
 * - block         → não prosseguir
 */
export function evaluate3DSStatus(status) {
  switch (status) {
    case 'Y': // Autenticada com sucesso
      return {
        action: 'proceed',
        message: 'Transação autenticada.',
      };
    case 'A': // Tentativa aceita
      return {
        action: 'proceed',
        message: 'Tentativa aceita (liability shift).',
      };
    case 'U': // Indisponível
      return {
        action: 'proceed_risk',
        message: '3DS indisponível (seguir por risco do lojista).',
      };
    case 'I': // Apenas informativo
      return {
        action: 'proceed_risk',
        message: 'Apenas informacional (sem autenticação).',
      };
    case 'C': // Challenge iniciado, mas não concluído
      return {
        action: 'challenge',
        message: 'Desafio solicitado (repetir challenge).',
      };
    case 'N': // Não autenticada
    case 'R': // Rejeitada
    default:
      return {
        action: 'block',
        message: 'Não autenticada/negada.',
      };
  }
}
